import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, CheckCircle2, Circle, ArrowUpRight, X, PartyPopper, Info } from "lucide-react";
import api from "@/lib/api";

/**
 * Founder First-Win — a personalized 3-step launch checklist for new paid subscribers.
 * Steps + completion come from the backend (deterministic, auto-detected). Dismissible,
 * persistent, mobile-friendly, and never blocks normal platform use.
 */
export function FirstWin() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/retention/first-win").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data || !data.exists || data.dismissed || hidden) return null;
  const { steps = [], progress = { done: 0, total: 0 }, completed, next_action } = data;
  if (!steps.length) return null;

  const dismiss = () => {
    setHidden(true);
    api.post("/retention/first-win/dismiss", { dismissed: true }).catch(() => {});
  };
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6"
      data-testid="first-win">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0">
            {completed ? <PartyPopper className="w-5 h-5 text-[#0A0A0B]" /> : <Rocket className="w-5 h-5 text-[#0A0A0B]" />}
          </div>
          <div>
            <div className="text-white font-heading text-lg leading-tight">
              {completed ? "You nailed your First Win! 🎉" : "Your First Win"}
            </div>
            <div className="text-[11px] text-[#A1A1AA]">
              {completed ? "Great momentum — here's your best next move." : "Three quick steps to get real value from day one."}
            </div>
          </div>
        </div>
        <button onClick={dismiss} data-testid="first-win-dismiss" className="text-[#71717A] hover:text-white p-1 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-2 rounded-full bg-white/8 overflow-hidden">
          <motion.div className="h-full fv-gold-gradient rounded-full" initial={{ width: 0 }}
            animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} />
        </div>
        <span className="text-xs font-semibold text-[#D4AF37] shrink-0" data-testid="first-win-progress">
          {progress.done}/{progress.total}
        </span>
      </div>

      <AnimatePresence>
        {!completed ? (
          <div className="space-y-2.5">
            {steps.map((s, i) => (
              <div key={s.id} data-testid={`first-win-step-${s.id}`}
                className={`flex items-start gap-3 rounded-xl border p-3.5 ${s.done ? "border-emerald-500/25 bg-emerald-500/5" : "border-white/8 bg-[#0f0f11]"}`}>
                {s.done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  : <Circle className="w-5 h-5 text-[#52525B] mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-bold text-[#71717A]">STEP {i + 1}</span>
                    {s.module && <span className="text-[10px] text-[#52525B]">· {s.module}</span>}
                  </div>
                  <div className={`text-sm font-medium ${s.done ? "text-[#A1A1AA] line-through" : "text-white"}`}>{s.title}</div>
                  {!s.done && <p className="text-[11px] text-[#A1A1AA] mt-0.5 leading-relaxed">{s.why}</p>}
                </div>
                {!s.done && (
                  <button onClick={() => navigate(s.action_path)} data-testid={`first-win-go-${s.id}`}
                    className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-4 h-8 text-xs flex items-center gap-1 shrink-0 self-center">
                    {s.action_label} <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-4" data-testid="first-win-complete">
            <p className="text-sm text-[#D4D4D8] mb-3">
              You've completed your first key milestones. Keep the momentum going:
            </p>
            {next_action?.title && (
              <>
                <div className="text-white font-medium text-sm">{next_action.title}</div>
                {next_action.why && (
                  <p className="text-[11px] text-[#A1A1AA] mt-1 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#D4AF37]" /> {next_action.why}
                  </p>
                )}
                <button onClick={() => navigate(next_action.action_path || "/dashboard")} data-testid="first-win-next-action"
                  className="mt-3 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 h-9 text-sm flex items-center gap-1.5">
                  {next_action.action_label || "Continue"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
